import React, { useContext } from 'react';
import CIcon from '@coreui/icons-react';
import { EventExplorerContext } from '../../EventExplorer';
import CTooltip from '../../../../../migration/CTooltip';
import { generateCustomDimensionName, getOriginalField } from '../../../../../../utils';
import { useDispatch } from 'react-redux';
import { setFlexibleModal } from '../../../../../../actions/common';
import { COMPONENT_NAME, SECTION_REPORT } from '../../../../../../constants';
import PropTypes from 'prop-types';

const IntentPropertyTooltip = ({ title, description, fieldName }) => {
     const dispatch = useDispatch();
     const { intentProperties } = useContext(EventExplorerContext);
     const intentProperty = intentProperties?.find((item) => generateCustomDimensionName(`${item?.name}_intent`) === fieldName);
     const intentRuleId = intentProperty?.ruleId;

     const handleOpenIntentRuleModal = () => {
          if (!intentRuleId) return;

          dispatch(
               setFlexibleModal({
                    show: true,
                    component: COMPONENT_NAME.INTENT_ANALYSIS_MANAGE_INTENT_RULES,
                    attributes: { ruleId: intentRuleId },
               })
          );
     };

     return (
          <div className='position-relative'>
               <CTooltip
                    interactive={true}
                    advancedOptions={{
                         zIndex: 9999,
                         appendTo: document.body,
                         onShow(instance) {
                              setTimeout(() => {
                                   const tooltipEl = instance.popper.querySelector('[data-view-rule]');
                                   if (tooltipEl) {
                                        tooltipEl.addEventListener('click', (e) => {
                                             e.preventDefault();

                                             // Hide tooltip before opening modal
                                             if (instance?.hide) {
                                                  instance.hide();
                                             }

                                             handleOpenIntentRuleModal();
                                        });
                                   }
                              }, 0);
                         },
                    }}
                    content={`
                         <div>
                              <strong>AI Generated</strong>
                              ${description ? `<div>${description}</div>` : ''}
                              ${
                                   intentRuleId
                                        ? `<div>
                                                  <a 
                                                       href="#" 
                                                       data-view-rule="true"
                                                       style="color: white; text-decoration: underline; cursor: pointer;"
                                                  >
                                                       View Rule &gt;
                                                  </a>
                                             </div>`
                                        : ''
                              }
                         </div>
                    `}
               >
                    <span>{title}</span>
               </CTooltip>
          </div>
     );
};

const HeaderCellTooltip = ({ fieldData, title }) => {
     const { section, description, value } = fieldData || {};

     if (section === SECTION_REPORT.INTENT_PROPERTIES) {
          return <IntentPropertyTooltip title={title} description={description} fieldName={value} />;
     }

     if (!description) {
          return <>{title}</>;
     }

     return (
          <CTooltip content={description}>
               <span>{title}</span>
          </CTooltip>
     );
};

function CustomHeaderCell({ props, dimensions, arrShowShortIds, showLock, lock, showShortId, sort, disableSort, setLock, setShowShortId, setOpenTooltip }) {
     let { field, title } = props;
     const { listSegmentReportApply, setAttributeBeforeReload } = useContext(EventExplorerContext);
     const showEyeIcon = dimensions[0] && dimensions.some((item) => arrShowShortIds.includes(item.key) && item.key === field);
     const { setSort, filterMetric, filterProperties, filterDimensions } = useContext(EventExplorerContext);

     const handleClickSort = (field) => {
          if (field === 'viewDetail' || disableSort) {
               return;
          }
          if (sort.fieldName !== field) {
               setSort({ fieldName: field, isDesc: true });
          } else {
               if (sort.isDesc) setSort({ fieldName: field, isDesc: false });
               else {
                    setSort({});
               }
          }
          setAttributeBeforeReload({
               dimensions: [],
               properties: [],
               metrics: [],
               scoped: '',
          });
     };

     const handleClickLock = (field, event) => {
          event.stopPropagation();
          if (lock[field]) {
               setLock((old) => {
                    return { ...old, [field]: !lock[field] };
               });
          } else {
               setLock((old) => {
                    return { ...old, [field]: true };
               });
          }
     };

     const handleShowShortId = (e, field) => {
          let newShowShortId = [...showShortId];

          if (showShortId.includes(field)) {
               newShowShortId = [...showShortId].filter((item) => item !== field);
          } else {
               newShowShortId.push(field);
          }

          e.stopPropagation();
          setShowShortId(newShowShortId);
     };

     const fieldData =
          filterMetric?.find((item) => getOriginalField(field, listSegmentReportApply) === item.value) ||
          filterProperties?.find((item) => field === item.value) ||
          filterDimensions?.find((item) => field === item.value);

     return (
          <span className='k-cell-inner table-head'>
               <span className='k-link'>
                    <span onClick={() => handleClickSort(field)}>
                         <HeaderCellTooltip fieldData={fieldData} title={title} />
                    </span>
                    <div className={`lock-column ${showLock[field] && 'show'} ${lock[field] && 'active'}`} onClick={(event) => handleClickLock(field, event)}>
                         <CIcon icon='iconLock' className='icon-lock'></CIcon>
                    </div>
                    {showEyeIcon && (
                         <div
                              onClick={(e) => {
                                   setOpenTooltip(false);
                                   handleShowShortId(e, field);
                              }}
                         >
                              <CIcon icon={showShortId.includes(field) ? 'iconEyeRegularSlash' : 'iconEyeRegular'}></CIcon>
                         </div>
                    )}

                    {sort.fieldName === field && !disableSort && <CIcon icon='iconDropdown' className={`icon-sort ${sort.isDesc ? 'desc' : 'asc'}`}></CIcon>}
               </span>
          </span>
     );
}

IntentPropertyTooltip.propTypes = {
     title: PropTypes.string,
     description: PropTypes.string,
     fieldName: PropTypes.string,
};

HeaderCellTooltip.propTypes = {
     fieldData: PropTypes.object,
     title: PropTypes.string,
};

CustomHeaderCell.propTypes = {
     props: PropTypes.object,
     dimensions: PropTypes.array,
     arrShowShortIds: PropTypes.array,
     showLock: PropTypes.object,
     lock: PropTypes.object,
     showShortId: PropTypes.array,
     sort: PropTypes.object,
     disableSort: PropTypes.bool,
     setLock: PropTypes.func,
     setShowShortId: PropTypes.func,
     setOpenTooltip: PropTypes.func,
};

export default CustomHeaderCell;
